import { cn } from "@/lib/utils";
import { useId } from "react";

/** Corner glow. `warm` is peach into lilac. `cool` stays in the lilac. */
export type SwooshTone = "warm" | "cool";

const SWOOSH_STOPS: Record<
  SwooshTone,
  { start: string; mid: string; end: string }
> = {
  warm: { start: "#fde4df", mid: "#f6ebf4", end: "#e9e7fb" },
  cool: { start: "#ddd6fb", mid: "#ece7fb", end: "#f6f3fd" },
};

function SwooshRadialGradient({ id, tone }: { id: string; tone: SwooshTone }) {
  const stops = SWOOSH_STOPS[tone];
  return (
    <radialGradient
      id={id}
      cx="0"
      cy="0"
      r="0.8"
      gradientTransform="scale(1 2.2)"
    >
      <stop offset="0" stopColor={stops.start} />
      <stop offset="0.12" stopColor={stops.start} />
      <stop offset="0.5" stopColor={stops.mid} />
      <stop offset="1" stopColor={stops.end} />
    </radialGradient>
  );
}

/**
 * The Counselor Home swoosh: a wave across the top of a page with a coral glow
 * from the top-left corner fading to lavender ("1b corner glow"). Turned
 * 180° for the bottom of a section, so the coral glows from the bottom-right.
 * Absolutely positioned, so put it first inside a `relative isolate`
 * container that spans the full width; a bottom swoosh's container also needs
 * `overflow-hidden`, since the layer is taller than most bottom sections.
 */
export const SwooshBackdrop = ({
  corner = "top-left",
  isPageHeight = false,
  fit = "page",
  tone = "warm",
}: {
  /** Where the coral glows from, and so which edge the layer is anchored to. */
  corner?: "top-left" | "bottom-right";
  /** Stretch over the whole container, as on Counselor Home, instead of 1100px. */
  isPageHeight?: boolean;
  /**
   * `page` is the tall catalog swoosh. `hero` fills a short header with the
   * gradient and draws a fixed-height wave along the bottom, so a longer
   * title grows the header without pulling the curve up into the text.
   */
  fit?: "page" | "hero";
  /** `warm` is the default peach glow. `cool` is a lilac glow. */
  tone?: SwooshTone;
}) => {
  const gradientId = useId();
  const isTopLeft = corner === "top-left";
  const isHero = fit === "hero";
  const path = (
    <path
      d="M0 350 C 240 430, 440 440, 600 345 C 760 250, 860 200, 1000 200 L1000 0 L0 0 Z"
      fill={`url(#${gradientId})`}
    />
  );
  if (isHero && isTopLeft) {
    return (
      <>
        <svg
          aria-hidden
          className="pointer-events-none absolute inset-0 -z-10 h-full w-full"
          viewBox="0 0 1 1"
          preserveAspectRatio="none"
        >
          <defs>
            <SwooshRadialGradient id={gradientId} tone={tone} />
          </defs>
          <rect width="1" height="1" fill={`url(#${gradientId})`} />
        </svg>
        <svg
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0 -z-10 h-16 w-full md:h-20"
          viewBox="0 0 1000 80"
          preserveAspectRatio="none"
        >
          <path
            d="M0 34 C 220 70, 420 78, 600 38 C 760 6, 880 0, 1000 2 L1000 80 L0 80 Z"
            fill="hsl(var(--background))"
          />
        </svg>
      </>
    );
  }
  return (
    <svg
      aria-hidden
      className={cn(
        "pointer-events-none absolute inset-x-0 -z-10 w-full",
        isTopLeft ? "top-0" : "bottom-0",
        // Page height sets how deep the curve dips. Hero fit returns above.
        isPageHeight ? "inset-0 h-full" : "h-[86vw] md:h-[1100px]",
      )}
      viewBox="0 0 1000 1000"
      preserveAspectRatio="none"
    >
      <defs>
        <SwooshRadialGradient id={gradientId} tone={tone} />
      </defs>
      {isTopLeft ? path : <g transform="rotate(180 500 500)">{path}</g>}
    </svg>
  );
};

/**
 * The swoosh's gradient filling its parent, for a band that isn't wave-shaped:
 * coral in the top-left corner, or mirrored into the bottom-right for a
 * bottom band. Put it first inside a `relative isolate` container.
 */
export const SwooshGradientFill = ({
  corner,
  tone = "warm",
}: {
  corner: "top-left" | "bottom-right";
  /** `warm` is the default peach glow. `cool` is a lilac glow. */
  tone?: SwooshTone;
}) => {
  const gradientId = useId();
  const isTopLeft = corner === "top-left";
  const stops = SWOOSH_STOPS[tone];
  return (
    <svg
      aria-hidden
      className="pointer-events-none absolute inset-0 -z-10 h-full w-full"
      viewBox="0 0 1 1"
      preserveAspectRatio="none"
    >
      <defs>
        <linearGradient
          id={gradientId}
          x1={isTopLeft ? 0 : 1}
          y1={isTopLeft ? 0 : 1}
          x2={isTopLeft ? 1 : 0}
          y2={isTopLeft ? 0.35 : 0.65}
        >
          <stop offset="0" stopColor={stops.start} />
          <stop offset="0.24" stopColor={stops.mid} />
          <stop offset="1" stopColor={stops.end} />
        </linearGradient>
      </defs>
      <rect width="1" height="1" fill={`url(#${gradientId})`} />
    </svg>
  );
};
